import React, { useEffect } from 'react';
import { EventKind, ObjectKind, useEventsQuery } from 'src/graphql/_generated';
import styled from 'styled-components';
import { motion, useMotionValue, animate, useTransform, PanInfo } from 'framer-motion';
import { CommentSkeleton, Poppins } from 'src/common';
import { ReactComponent as CrossIcon } from 'src/assets/cross.svg';
import { AddComment, Comment } from './comps';
import { useAuth } from 'src/state/auth';

const CloseIcon = styled(CrossIcon)`
  cursor: pointer;
  fill: ${({ theme }) => theme.colors.prussianBlue};

  &:hover {
    fill: ${({ theme }) => theme.colors.gp1};
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 11;

  .content {
    height: 100%;
    display: flex;
    justify-content: flex-end;

    &__body {
      min-width: 560px;
      max-width: 560px;
      height: 100%;
      display: flex;
      flex-direction: column;
      background-color: ${({ theme }) => theme.colors.white};
      filter: drop-shadow(0px 0px 20px ${({ theme }) => theme.colors.shadowLight});

      &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        min-height: 86px;
        background-color: ${({ theme }) => theme.colors.aliceBlue};
        padding: 0 22px;
      }
      &__add-comment {
        padding: 22px;
        padding-bottom: 10px;
      }
      &__comments {
        overflow-y: auto;
        overflow-x: hidden;
        .comment:not(:last-child) {
          border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
        }
      }
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .content {
      &__body {
        &__header {
          height: 56px;
          padding: 0 18px;
        }
        &__add-comment {
          padding: 18px;
          padding-bottom: 10px;
        }
        &__comments {
        }
      }
    }
  }
`;

interface CommentsProps {
  onClose: () => void;
  eventData: { objectType: ObjectKind; objectId: string };
  refetch: () => void;
}

const Comments: React.FC<CommentsProps> = ({ onClose, eventData, refetch }) => {
  const x = useMotionValue(560);
  const snapThreshold = 100;
  const { workspaceId } = useAuth();

  const { data, loading, refetch: refetchComments } = useEventsQuery({
    variables: {
      workspaceId: workspaceId!,
      objectType: eventData.objectType,
      objectId: eventData.objectId,
      eventType: EventKind.Comment,
    },
  });

  const comments = data?.events || [];

  const backgroundOpacity = useTransform(x, [0, 560], [0.25, 0]);
  const backgroundColor = useTransform(backgroundOpacity, (opacity) => `rgba(0, 0, 0, ${opacity})`);

  const animateModalOpen = () => {
    animate(x, 0, {
      type: 'spring',
      stiffness: 300,
      damping: 30,
    });
  };

  const animateModalClose = () => {
    animate(x, 560, {
      type: 'spring',
      stiffness: 300,
      damping: 30,
      onComplete: () => {
        onClose();
      },
    });
  };

  const handleDragEnd = (_: MouseEvent, info: PanInfo) => {
    if (info.offset.x >= snapThreshold || info.velocity.x > 560) {
      animateModalClose();
    } else {
      animateModalOpen();
    }
  };

  useEffect(() => {
    x.set(560);
    animateModalOpen();
  }, []);

  const handleCommentAdded = () => {
    refetchComments();
    refetch();
  };

  return (
    <Overlay style={{ backgroundColor }}>
      <motion.div
        className="content"
        style={{ x }}
        drag="x"
        dragConstraints={{ left: 0 }}
        dragElastic={0}
        onDragEnd={handleDragEnd}
      >
        <motion.div className="content__body" data-cy="comments-drawer">
          <div className="content__body__header">
            <Poppins px={20}>Comments</Poppins>
            <CloseIcon onClick={animateModalClose} data-cy="comments-close" />
          </div>
          <div className="content__body__add-comment">
            <AddComment eventData={eventData} onCommentAdded={handleCommentAdded} />
          </div>

          <div className="content__body__comments">
            {loading && !data ? (
              <CommentSkeleton />
            ) : (
              comments.map((event) => (
                <Comment key={event.id} event={event} eventData={eventData} onMutate={handleCommentAdded} />
              ))
            )}
          </div>
        </motion.div>
      </motion.div>
    </Overlay>
  );
};

export default Comments;
